// property-images-hub.ts — Alpine controller backing PropertyImagesSection's
// wrapper AND PropertyImagesCurationPanel's root (the wizard drawer embed,
// images?embed=drawer) — both in property_hub_images.templ. Was a bare inline x-data object
// literal ({ activeTab: 'manage' }); promoted to a registered component so it
// can own editPano(id, kind) — the 360°-badge trigger Task 8 wired onto BOTH
// the canonical-image grid (canonicalImagesGrid) and, by Alpine's ordinary
// nested-scope inheritance, the curation modal's candidate tiles nested
// inside this same wrapper (PropertyCurationModal — its own x-data scope
// falls back to this one for any identifier it doesn't define itself, which
// is exactly how editPano resolves there without curation-modal.ts knowing
// anything about panos).
//
// Deliberately NOT a clone of the CMS picker's editPano/panoDraft takeover
// pane (image-picker.ts): this bespoke grid has no client-side reactive array
// of canonical images to seed a draft from (it's a plain Go for-loop), and a
// second full takeover pane here would duplicate UI for no real gain. Instead
// this is the "direct-post working affordance" alternative the task-9 brief
// explicitly allows for the hub surfaces — the caller (Go template) already
// knows the CURRENT media_kind at render time, so it passes the TARGET kind
// (the opposite) as the second argument; editPano just PATCHes it and lets
// the section's existing hx-trigger="property-images-updated from:body"
// self-refresh do the rest (same event uploader.ts already dispatches on
// upload completion — see its "Grid/panel self-refreshes" comment).
import { savePanoOverride } from "./pano-override";

export interface PropertyImagesHubConfig {
  // Property-hub base ("/organizations/{org}/properties/{slug}") — the SAME
  // base PropertyImagesData.BaseURL / CurationData.BaseURL already carry for
  // the hero/reorder/remove/curate routes on this exact page.
  baseURL: string;
  // Localized failure toast (server-built via t()) — no hardcoded English
  // literal, mirrors every other saveFailedMsg threaded through this file's
  // siblings (curation-modal.ts, image-credits.ts).
  saveFailedMsg?: string;
}

export function propertyImagesHub(config: PropertyImagesHubConfig) {
  return {
    activeTab: "manage" as string,
    baseURL: config.baseURL,
    saveFailedMsg: config.saveFailedMsg || "",

    async editPano(id: string, kind: string) {
      if (!id || (kind !== "photo" && kind !== "pano")) return;
      const ok = await savePanoOverride(`${this.baseURL}/images`, id, kind, this.saveFailedMsg);
      if (!ok) return;
      // Mirrors uploader.ts's own post-upload refresh dispatch — the section
      // wrapper (property_hub_images.templ) already listens for this on
      // `body` and re-fetches itself, so the flipped badge (and the curation
      // modal's candidate copy, next time it opens) reflect the server state
      // without a full page reload.
      document.body.dispatchEvent(new CustomEvent("property-images-updated", { bubbles: true }));
    },
  };
}
